/**
 * Masonry layout for CSS grid containers marked `data-masonry`.
 *
 * Ported from benpazdernik.com, which is the build reference for this rework.
 *
 * Bails if the browser supports native masonry (display: grid-lanes
 * or grid-template-rows: masonry) — CSS @supports takes over.
 *
 * Otherwise: measures each image's natural rendered height (via width/
 * height attrs → intrinsic ratio, available pre-load → no CLS), then
 * switches the grid to 0px auto-rows and spans each item so its visual
 * height ≈ image height. The row-gap doubles as the quantization unit.
 *
 * Formula: with grid-auto-rows: 0 and row-gap: G, span N occupies
 *   (N-1) × G pixels. For image height D: N = ceil(D / G) + 1.
 *
 * Why measure the IMG and not the item: in the pre-masonry (auto-rows)
 * state, items stretch via align-self: stretch to the tallest item in
 * their row → measuring the item returns row height, not natural item
 * height. The img itself ignores parent stretching; its BCR is the true
 * content height (column width × intrinsic ratio).
 *
 * ResizeObserver re-lays out only on container WIDTH changes. Height
 * deltas (caused by our own span-setting or image loads) are ignored to
 * prevent an infinite observer loop.
 */

/* `data-masonry-grid` until step 5b: isotope-3.0.6.pkgd.min.js bundles Masonry
   v4, which auto-initialises every element carrying `data-masonry` and lays it
   out with absolute positioning. Two masonries then fought over the same
   container and the grid collapsed into overlapping tiles -- measured on
   /hochzeitstorten.html. Isotope is gone, so the attribute is the plain one. */
const SELECTOR = '[data-masonry]'

function nativeMasonrySupported () {
  return (
    CSS.supports('display', 'grid-lanes') ||
    CSS.supports('grid-template-rows', 'masonry')
  )
}

function layoutGrid (grid) {
  if (getComputedStyle(grid).display !== 'grid') {
    grid.style.gridAutoRows = ''
    for (const item of grid.children) item.style.gridRowEnd = ''
    return
  }

  const items = Array.from(grid.children)
  const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 1

  const heights = items.map((item) => {
    const img = item.querySelector('img')
    return img
      ? img.getBoundingClientRect().height
      : item.getBoundingClientRect().height
  })

  grid.style.gridAutoRows = '0px'
  items.forEach((item, i) => {
    if (heights[i] <= 0) return
    const value = `span ${Math.ceil(heights[i] / rowGap) + 1}`
    if (item.style.gridRowEnd !== value) item.style.gridRowEnd = value
  })
}

export function initMasonry (root = document) {
  if (nativeMasonrySupported()) return

  const grids = root.querySelectorAll(SELECTOR)
  if (!grids.length) return

  const widths = new WeakMap()

  grids.forEach((grid) => {
    widths.set(grid, grid.getBoundingClientRect().width)
    layoutGrid(grid)
  })

  /* An image arriving after layout would otherwise overlap its neighbour.
     Load events do not bubble, so they are bound per image rather than
     delegated from the grid. */
  root.querySelectorAll(`${SELECTOR} img`).forEach((img) => {
    if (img.complete) return
    img.addEventListener(
      'load',
      () => {
        const grid = img.closest(SELECTOR)
        if (grid) layoutGrid(grid)
      },
      { once: true }
    )
  })

  const ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const w = entry.contentRect.width
      if (widths.get(entry.target) === w) continue
      widths.set(entry.target, w)
      layoutGrid(entry.target)
    }
  })
  grids.forEach((grid) => ro.observe(grid))

  /* See reveal.js: the observer outlives the grids unless it is told not to.
     The per-image `load` listeners are `once` and bound to elements that go
     with the container, so they need no help. */
  return () => ro.disconnect()
}
